import { FC, ReactNode, useMemo } from 'react';
import useThread from '@/hooks/use-thread.hook';
import { ThreadMessageModel } from '@/lib/models/thread/thread-message.model';
import ThreadMessage from './thread-message';
import { Box, Fade, Stack } from '@mui/material';
import LoaderDots from '@/components/loader-dots';
import Text from '@/components/text';
import { createUseStyles } from 'react-jss';
import { getTimestamp } from '@/lib/date-helpers';

const useStyles = createUseStyles({
  loaderControlMessage: {
    color: '#686868',
  },
});

interface Props {
  BadgeEl: ReactNode;
  roleTitle: string;
}

const StreamThreadMessage: FC<Props> = ({ BadgeEl, roleTitle }) => {
  const styles = useStyles();
  const { thread, controlSignal, status: threadStatus, streamContent } = useThread();

  const messageTimestamp = useMemo(() => getTimestamp(new Date(), true), []);

  const message = useMemo(() => {
    const threadMessage = new ThreadMessageModel({
      threadId: thread.id,
      role: 'system',
      timestamp: messageTimestamp,
      onDeck: true,
    });

    threadMessage.id = 'system-deck-message';

    if (threadStatus === 'stream' && streamContent) {
      threadMessage.content = streamContent;
    } else {
      const hasDatasourceReview = controlSignal === 'data-source-review';

      threadMessage.component = (
        <Stack gap={2} direction="row" alignItems="flex-end" height={25}>
          <LoaderDots />
          {hasDatasourceReview && (
            <Fade in={true}>
              <Box>
                <Text italic size="small" className={styles.loaderControlMessage}>
                  Reviewing data sources
                </Text>
              </Box>
            </Fade>
          )}
        </Stack>
      );
    }

    return threadMessage;
  }, [
    controlSignal,
    threadStatus,
    streamContent,
    styles.loaderControlMessage,
    thread.id,
    messageTimestamp,
  ]);

  return (
    <ThreadMessage
      BadgeEl={BadgeEl}
      message={message}
      roleTitle={roleTitle}
      hideToolbar
      fullHeight
    />
  );
};

export default StreamThreadMessage;
